import { useQuery } from '@tanstack/react-query';
import adjudicatorsService, {
  type AdjudicatorLookupResponse,
  type ClientIdCheckResponse,
} from '../../services/adjudicatorsService';

/**
 * A plan's BIN and carrier for the directory: a 6-digit BIN (a PMS may hand it
 * back as a number) and the carrier as stored, trimmed (1 to 4 letters or
 * digits). The backend's `planKeyOf` owns every normalisation (case, `2` read
 * as `02`) and says when it made one. Anything else answers `null`: it is not
 * looked up, never guessed at.
 */
export function directoryKeyOf(
  adjHostId: unknown,
  carrierId: unknown,
): { bin: string; carrier: string } | null {
  const bin = adjHostId == null ? '' : String(adjHostId).trim();
  const carrier = carrierId == null ? '' : String(carrierId).trim();
  if (!/^\d{6}$/.test(bin)) return null;
  if (carrier && !/^[A-Za-z0-9]{1,4}$/.test(carrier)) return null;
  return { bin, carrier };
}

/** Reference data that changes only with a deploy: cached for an hour. */
const ONE_HOUR = 60 * 60 * 1000;

/**
 * The one definition of the directory lookup query, so `useAdjudicatorLookup`
 * and a `useQueries` over several plans (PRX-130) share one cache entry.
 */
export function adjudicatorLookupQueryOptions(
  pharmacyId: string | null | undefined,
  key: { bin: string; carrier: string } | null,
  options?: { enabled?: boolean },
) {
  return {
    queryKey: ['adjudicators', 'lookup', pharmacyId, key?.bin, key?.carrier],
    queryFn: (): Promise<AdjudicatorLookupResponse> =>
      adjudicatorsService.lookup(pharmacyId as string, key!.bin, key!.carrier),
    enabled: options?.enabled !== false && !!pharmacyId && !!key,
    staleTime: ONE_HOUR,
  };
}

/** PRX-128 — the adjudicator directory rows for one BIN + carrier. */
export function useAdjudicatorLookup(
  pharmacyId: string | null | undefined,
  key: { bin: string; carrier: string } | null,
  options?: { enabled?: boolean },
) {
  return useQuery<AdjudicatorLookupResponse>(adjudicatorLookupQueryOptions(pharmacyId, key, options));
}

/**
 * PRX-128 — a client ID checked against the plan's rule. A POST, but a pure
 * check with no side effect, so it is cached like a read.
 */
export function useClientIdCheck(
  pharmacyId: string | null | undefined,
  key: { bin: string; carrier: string } | null,
  clientId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<ClientIdCheckResponse>({
    queryKey: ['adjudicators', 'client-id-check', pharmacyId, key?.bin, key?.carrier, clientId],
    queryFn: () =>
      adjudicatorsService.checkClientId(pharmacyId as string, {
        bin: key!.bin,
        ...(key!.carrier ? { carrierId: key!.carrier } : {}),
        clientId,
      }),
    enabled: options?.enabled !== false && !!pharmacyId && !!key && clientId.trim().length > 0,
    staleTime: ONE_HOUR,
  });
}
